import { Space } from "antd";
import { FC, useEffect, useState } from "react";
import { useLoading } from "../../context/LoadingProvider/useLoading";
import { ActionButtonEnum } from "../../enums/actionButtonEnum";
import { SearchOptionEnum } from "../../enums/searchOptionEnum";
import useActionButtons from "../../hooks/useActionButtons";
import useSmartSearchParams, {
  SmartSearchParams,
} from "../../hooks/useSmartSearchParams";
import { Output } from "../../services/types";
import { isString } from "../../utils/stringUtils";
import Button from "../UI/Button";
import SmartSearch from "../UI/SmartSearch";
import DefaultFormParserLayout from "./default-form-parser-layout";
import { ValidationEnum } from "../../enums/validationEnum";

export type SmartSearchAndActionsLayoutProps = {
  searchBy: SearchOptionEnum[];
  labels?: string[];
  validOnEmpty?: boolean;
  actions: ActionButtonEnum[];
  validations?: ValidationEnum[];
  onRefresh: (
    output: Output,
    params: SmartSearchParams
  ) => Promise<string | void>;
};

const SmartSearchAndActionsLayout: FC<SmartSearchAndActionsLayoutProps> = ({
  searchBy,
  labels = ["active", "closed"],
  validOnEmpty = true,
  actions,
  validations,
  onRefresh,
}) => {
  const loading = useLoading();

  const [params, setParams] = useSmartSearchParams();

  const {
    isToShowRefreshAction,
    isToShowSaveCSVAction,
    isToShowSavePDFAction,
  } = useActionButtons(actions);

  const [search, setSearch] = useState("");
  const [validSearch, setValidSearch] = useState(false);
  const [report, setReport] = useState("");

  const isToValidateSmartSearch =
    validations?.includes(ValidationEnum.VALIDATE_SMART_SEARCH) && !validSearch;

  const isToValidateSearchInput =
    validations?.includes(ValidationEnum.VALIDATE_SEARCH) && !search.length;

  const disabled =
    isToValidateSmartSearch ||
    isToValidateSearchInput ||
    (search.length > 0 && !validSearch);

  const handleEnter = (event: KeyboardEvent) => {
    if (loading.loading) return;
    if (!event.repeat && event.key === 'Enter' && disabled && validSearch) {
      refreshReport()
    }
  }

  useEffect(() => {
    document.addEventListener("keydown", handleEnter)
    return () => document.removeEventListener("keydown", handleEnter)
  })

  const refreshReport = async (output: Output = Output.HTML) => {
    loading.addLoading();

    const res = await onRefresh(output, params);

    if (Output.isHTML(output) && isString(res)) {
      setReport(res);
    }

    loading.removeLoading();
  };

  return (
    <DefaultFormParserLayout html={report}>
      <Space direction="horizontal" size="small" wrap>
        <SmartSearch
          autoFocus
          validOnEmpty={validOnEmpty}
          label="Smart Search"
          status={labels?.join(",")}
          searchBy={searchBy}
          onChange={(e: string) => setSearch(e)}
          value={search}
          onValidSearch={setValidSearch}
          onObjValueChange={(e: any) => setParams({ ...params, ...e })}
        />

        {isToShowRefreshAction && (
          <Button
            type="primary"
            disabled={disabled}
            onClick={() => refreshReport()}
          >
            Refresh
          </Button>
        )}

        {isToShowSaveCSVAction && (
          <Button
            type="primary"
            disabled={disabled}
            onClick={() => refreshReport(Output.CSV)}
          >
            Save as CSV
          </Button>
        )}

        {isToShowSavePDFAction && (
          <Button
            type="primary"
            disabled={disabled}
            onClick={() => refreshReport(Output.PDF)}
          >
            Save as PDF
          </Button>
        )}
      </Space>
    </DefaultFormParserLayout>
  );
};

export default SmartSearchAndActionsLayout;
